import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { useTypeDistribution } from '@/hooks/dashboard/useTypeDistribution'
import { bookListUrl } from '@/lib/utils'
import { ChartContainer } from '../ChartContainer'

// Types for Recharts custom components
interface TooltipPayload {
  value: number
  payload: {
    name: string
    value: number
    percentage: number
    color: string
  }
}

interface CustomTooltipProps {
  active?: boolean
  payload?: TooltipPayload[]
}

interface LegendPayload {
  value: string
  color: string
  payload: {
    percentage: number
  }
}

interface CustomLegendProps {
  payload?: LegendPayload[]
}

export function TypeDistributionChart() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data, totalBooks, isLoading, error } = useTypeDistribution()

  const title = t('dashboard.charts.typeDistribution')
  const description = `${totalBooks} ${t('dashboard.charts.booksTotal')}`

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: data.color }} />
            <p className="font-medium text-foreground">{data.name}</p>
          </div>
          <div className="space-y-1 text-sm">
            <p className="text-muted-foreground">
              {`${data.value} ${t('dashboard.charts.books')}`}
            </p>
            <p className="text-muted-foreground">
              {`${data.percentage.toFixed(1)}% ${t('dashboard.charts.ofTotal')}`}
            </p>
          </div>
        </div>
      )
    }
    return null
  }

  // Custom legend
  const CustomLegend = ({ payload }: CustomLegendProps) => {
    if (!payload || payload.length === 0) return null

    return (
      <div className="flex justify-center gap-6 mt-4">
        {payload.map((entry) => (
          <div key={entry.value} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-muted-foreground">{entry.value}</span>
            <span className="font-medium text-foreground">
              ({entry.payload.percentage.toFixed(1)}%)
            </span>
          </div>
        ))}
      </div>
    )
  }

  // Show empty state if not enough data
  if (!isLoading && !error && data.length === 0) {
    return (
      <ChartContainer title={title} description={t('dashboard.charts.noData')}>
        <div className="flex items-center justify-center h-full text-center text-muted-foreground">
          <div>
            <div className="text-4xl mb-4">📚</div>
            <p className="font-medium mb-1">{t('dashboard.charts.noDataTitle')}</p>
            <p className="text-sm">{t('dashboard.charts.addBooksToSeeDistribution')}</p>
          </div>
        </div>
      </ChartContainer>
    )
  }

  return (
    <ChartContainer
      title={title}
      description={description}
      isLoading={isLoading}
      error={error?.message || null}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="45%" // Adjusted to make room for legend
            innerRadius={40}
            outerRadius={80}
            paddingAngle={2}
            dataKey="value"
            animationBegin={0}
            animationDuration={800}
          >
            {data.map((entry, _index) => (
              <Cell
                key={entry.name}
                fill={entry.color}
                cursor="pointer"
                onClick={() => navigate(bookListUrl({ type: entry.type, status: 'all' }))}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend content={<CustomLegend />} />
        </PieChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}
